import type { PaymentMethod } from './sales';

/**
 * Operating expenses: what it costs to run the business, as opposed to what it costs to stock it.
 *
 * An expense in V1 is **direct-paid** — it records money that has already left, at the moment it is
 * posted. It is not a bill, so it has no due date, no outstanding balance and no settlement state,
 * and posting one creates nothing in purchasing or payables. An expense may name a supplier for
 * context, and that changes nothing about what the supplier is owed.
 */

export const ExpenseStatus = {
  POSTED: 'POSTED',
  VOIDED: 'VOIDED',
} as const;
export type ExpenseStatus = (typeof ExpenseStatus)[keyof typeof ExpenseStatus];

/**
 * The tender types an expense may record.
 *
 * The same subset supplier payments use, and for the same reasons: `WALLET` is a consumer tender
 * with no operating meaning, and a cheque settles nothing until it clears — which, for a document
 * that means "already paid", would be a contradiction.
 */
export const EXPENSE_PAYMENT_METHODS = ['CASH', 'BANK_TRANSFER', 'CARD', 'OTHER'] as const;
export type ExpensePaymentMethod = (typeof EXPENSE_PAYMENT_METHODS)[number];

/** Methods that cannot be reconciled later without the provider's own reference. */
export const EXPENSE_METHODS_REQUIRING_REFERENCE: readonly ExpensePaymentMethod[] = [
  'BANK_TRANSFER',
  'CARD',
  'OTHER',
];

export function isExpensePaymentMethod(value: PaymentMethod | string): boolean {
  return (EXPENSE_PAYMENT_METHODS as readonly string[]).includes(value);
}

export function expenseNeedsReference(method: ExpensePaymentMethod): boolean {
  return EXPENSE_METHODS_REQUIRING_REFERENCE.includes(method);
}

/**
 * A supplier as an expense form needs to see one.
 *
 * Reference data, not supplier management: enough to choose who was paid and to recognise the
 * choice afterwards, and nothing more. No phone, no email, no address, no tax number, no notes and
 * nothing commercial — an expense user has no business reading any of it, and this is the shape
 * that makes sure they cannot.
 */
export interface ExpenseSupplierOption {
  id: string;
  supplierNumber: string;
  name: string;
  nameAr: string | null;
  /** Inactive suppliers stay selectable: an expense is often entered after one is retired. */
  isActive: boolean;
}

/**
 * How the business classifies what it spends.
 *
 * Tenant-wide master data, not an application enum: rent, utilities and marketing are one tenant's
 * vocabulary, and the next tenant's is different. Deactivating a category stops new expenses using
 * it and changes no expense already recorded against it.
 */
export interface ExpenseCategorySummary {
  id: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** One expense as a list shows it. */
export interface ExpenseSummary {
  id: string;
  reference: string;
  status: ExpenseStatus;
  branchId: string;
  branchName: string;
  categoryId: string;
  categoryName: string;
  categoryNameAr: string | null;
  expenseDate: string;
  currencyCode: string;
  amountMinor: number;
  /** What it counts for right now: the amount while posted, zero once voided. Derived, never stored. */
  activeAmountMinor: number;
  method: ExpensePaymentMethod;
  /** The supplier named for context, if any. It is owed nothing by this document. */
  supplierId: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  /** Whoever was paid, where no supplier record exists. */
  payee: string | null;
  postedByName: string | null;
  postedAt: string;
  voidedAt: string | null;
}

export interface ExpenseDetail extends ExpenseSummary {
  /** The provider's own reference. Present only to a caller allowed to see it. */
  externalReference: string | null;
  notes: string | null;
  voidedReason: string | null;
  voidedByName: string | null;
}
